<script>
import Layout from '@layouts/adminLayout.vue'
import MottoIcon from '@components/motto-icon.vue'
import LoaderIcon from '@components/loader-icon.vue'
import MyAward from '@components/awards/my-award.vue'
import { authComputed } from '@state/helpers'
import { getMyAwards } from '@src/api/awards'
import { transformMany as transformAwards } from '@src/api/transformers/awardTransformer'
import { isMobileMixin } from '@src/mixins/isMobileMixin'
import _ from 'lodash'

export default {
  page: {
    title: 'My Awards',
    meta: [
      {
        name: 'description',
        content: 'The My Awards page.',
      },
    ],
  },
  components: {
    Layout,
    MottoIcon,
    LoaderIcon,
    MyAward,
  },
  mixins: [isMobileMixin],
  data() {
    return {
      loading: true,
      error: false,
      myAwards: [],
      awardType: 'User',
    }
  },

  computed: {
    ...authComputed,
    getMyAwards() {
      return _.chain(this.myAwards).filter({ type: this.awardType }).orderBy(['year', 'title'], ['desc', 'asc']).value()
    },

    title() {
      if (this.awardType === 'Office') {
        return 'My Office Awards'
      }

      return 'My Awards'
    },
  },

  created() {
    this.loadMyAwards()
  },

  methods: {
    async loadMyAwards() {
      try {
        const {
          data: { data: myAwards },
        } = await getMyAwards()
        this.myAwards = transformAwards(myAwards)
      } catch (err) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    pickAwardType(type) {
      this.awardType = type
    },
  },
}
</script>

<template>
  <Layout>
    <LoaderIcon v-if="loading || error" :error="error"></LoaderIcon>
    <div v-else>
      <div :class="$style.breadcrumbs">
        <RouterLink :to="{ name: 'awards' }">
          <MottoIcon :class="$style.backButton" name="Arrow" icon-color="gray" class="cursor-pointer" />
        </RouterLink>
        <RouterLink :to="{ name: 'awards' }">
          <p class="p2 color-darkest-gray cursor-pointer">AWARDS</p>
        </RouterLink>
        <BaseIcon class="color-gray" name="chevron-right" />
        <p :class="$style.selectedBreadcrumb" class="button-text color-primary-blue">MY AWARDS</p>
      </div>
      <div class="d-flex">
        <div :class="$style.myAwardsContainer">
          <h2 :class="$style.myAwardsTitle" class="color-primary-blue">{{ title }}</h2>
          <div v-if="isMobile" class="mb-4 d-flex">
            <BaseSelector :active="awardType === 'User'" class="mr-15" @click="pickAwardType('User')">
              Individual
            </BaseSelector>
            <BaseSelector :active="awardType === 'Office'" @click="pickAwardType('Office')">Office</BaseSelector>
          </div>
          <p v-if="!getMyAwards.length" class="text-center">You currently have no awards</p>
          <div v-if="getMyAwards.length" :class="$style.myAwards">
            <MyAward v-for="award in getMyAwards" :key="award.id" :award="award" />
          </div>
        </div>
        <div v-if="!isMobile" :class="$style.rightPanel">
          <div class="d-flex justify-content-around">
            <BaseSelector :active="awardType === 'User'" data-award-select-individual @click="pickAwardType('User')">
              Individual
            </BaseSelector>
            <BaseSelector :active="awardType === 'Office'" data-award-select-office @click="pickAwardType('Office')">
              Office
            </BaseSelector>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.myAwardsContainer {
  @media (max-width: $lg-breakpoint) {
    flex: 1 0 auto;
    width: 100%;
    margin-bottom: 10px;
  }

  flex: 1 0 740px;
}

.breadcrumbs {
  display: flex;
  align-items: center;
  margin-bottom: 40px;

  > svg {
    margin: -2px 13px 0;
  }
}

.backButton {
  margin-right: 29px;

  > svg {
    width: 30px;
    height: 30px;
  }
}

.selectedBreadcrumb {
  &::after {
    float: left;
    width: 100%;
    height: 4px;
    content: '';
    background: $primary-blue;
    border-radius: 5px;
  }
}

.myAwards {
  @media (max-width: $lg-breakpoint) {
    grid-template-columns: minmax(0, 1fr);
    color: $primary-charcoal;
  }

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(359px, 1fr));
  grid-gap: 32px 22px;
}

.myAwardsTitle {
  @media (max-width: $lg-breakpoint) {
    margin-bottom: 24px;
    font-size: 24px;
    color: $primary-charcoal;
  }

  margin-bottom: 32px;
}

/* RIGHT PANEL */

.rightPanel {
  @media (max-width: $lg-breakpoint) {
    display: none;
  }

  flex: 0 0 180px;

  > div {
    margin-top: -14px;
  }
}
</style>
